/**
 * PermissionGate Component
 * Conditionally renders children based on user permissions
 */

import React, { ReactNode } from 'react';
import { Text, makeStyles } from '@fluentui/react-components';
import { ShieldErrorRegular } from '@fluentui/react-icons';
import { usePermissions } from '../../contexts/PermissionContext';

const useStyles = makeStyles({
  deniedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px',
    gap: '12px',
  },
  deniedIcon: {
    fontSize: '48px',
    color: '#d13438',
  },
  deniedText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  deniedSubtext: {
    fontSize: '14px',
    color: '#605e5c',
  },
});

interface PermissionGateProps {
  children: ReactNode;
  privilege?: string;
  anyPrivilege?: string[];
  allPrivileges?: string[];
  fallback?: ReactNode;
  showDenied?: boolean;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  privilege,
  anyPrivilege,
  allPrivileges,
  fallback,
  showDenied = false,
}) => {
  const styles = useStyles();
  const { hasPrivilege, hasAnyPrivilege, hasAllPrivileges, loading } = usePermissions();

  if (loading) {
    return null;
  }

  let hasPermission = false;

  if (privilege) {
    hasPermission = hasPrivilege(privilege);
  } else if (anyPrivilege && anyPrivilege.length > 0) {
    hasPermission = hasAnyPrivilege(anyPrivilege);
  } else if (allPrivileges && allPrivileges.length > 0) {
    hasPermission = hasAllPrivileges(allPrivileges);
  } else {
    hasPermission = true;
  }

  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }

    if (showDenied) {
      return (
        <div className={styles.deniedContainer}>
          <ShieldErrorRegular className={styles.deniedIcon} />
          <Text className={styles.deniedText}>Access Denied</Text>
          <Text className={styles.deniedSubtext}>
            You don't have permission to view this content
          </Text>
        </div>
      );
    }

    return null;
  }

  return <>{children}</>;
};
